// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Giacenze per ubicazione"
// Centro assistenza → guida per operatori e amministratori, solo piano TRACE.
//
// Dove si trova ADESSO il prodotto in lavorazione, cella per cella, e come lo
// si sposta. Non le materie prime: quelle stanno nella guida "Il magazzino
// materie prime".
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette copiate dal
// codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ......... App.js, GiacenzeUbicazioni
//   Dati .............. GET /ubicazioni/giacenze — somma ENTRATA meno USCITA
//                       per (ubicazione, fase); HAVING > 0, quindi le righe a
//                       zero spariscono da sole.
//   Colonne ........... Ubicazione · Prodotto · Lotto · Fase · Pz · Peso (kg) · Sposta
//   Riferimento lav. .. "lav. #<id> del <data> · chiusa" sotto il lotto. Serve
//                       perche' due lavorazioni avviate lo stesso giorno
//                       condividono il lotto: senza il numero le righe sono
//                       indistinguibili.
//   Sposta ............ modale "Sposta prodotto" → POST
//                       /lavorazioni/fasi/:id/sposta. Due rifiuti possibili:
//                       "Quantità da spostare superiore alla giacenza
//                       dell'ubicazione (disponibili N pz / N kg)" e
//                       "La data indicata è precedente all'inizio della fase
//                       (GG/MM/AAAA HH:MM)".
//   Anagrafica ........ Tracciabilità · Configurazione · Ubicazioni (Nome,
//                       Descrizione, "Ubicazione attiva")
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'g0', label: 'Che cosa è questa schermata' },
  { id: 'g1', label: '1. Da dove arrivano le righe' },
  { id: 'g2', label: '2. Leggere la tabella' },
  { id: 'g3', label: '3. Perché c’è il numero' },
  { id: 'g4', label: '4. Spostare in un’altra cella' },
  { id: 'g5', label: '5. Quello che resta dopo' },
  { id: 'g6', label: '6. La merce di terzi' },
  { id: 'g7', label: 'Le trappole' },
  { id: 'g8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    blu:    { bg: LT.blu, fg: '#fff',  bd: LT.blu },
    neutro: { bg: LT.n0,  fg: LT.blu,  bd: LT.n200 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '5px 10px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, aiuto, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || '—'}</div>
    {aiuto && (
      <div style={{ fontSize: 10.5, color: LT.n500, marginTop: 3, lineHeight: 1.45 }}>{aiuto}</div>
    )}
  </div>
);

const TH = ({ children, destra }) => (
  <th style={{
    padding: '8px 10px', textAlign: destra ? 'right' : 'left',
    fontSize: 10, fontWeight: 700, color: LT.n500,
    textTransform: 'uppercase', letterSpacing: 0.3,
    borderBottom: `1px solid ${LT.n200}`, whiteSpace: 'nowrap',
  }}>{children}</th>
);

const TD = ({ children, destra, style }) => (
  <td style={{
    padding: '9px 10px', textAlign: destra ? 'right' : 'left',
    fontSize: 12.5, color: LT.navy, ...style,
  }}>{children}</td>
);

export default function Giacenze() {
  return (
    <GuidaLayout
      titolo="Giacenze per ubicazione"
      sottotitolo="Dove si trova adesso ogni lotto in lavorazione, cella per cella, e come si sposta da una all'altra. È la schermata che risponde alla domanda più banale e più frequente del reparto: dov'è finito quel prodotto."
      durata="~ 10 minuti"
      destinatario="Per operatori e amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* g0 */}
      <H id="g0">Che cosa è questa schermata</H>

      <P>
        <Code>Giacenze per ubicazione</Code>, nel gruppo Produzione, è la{' '}
        <Strong>fotografia di adesso</Strong>: quali lotti ci sono in ogni cella, in che fase
        sono, quanti pezzi e quanti chili.
      </P>
      <P>
        Riguarda il <Strong>prodotto in lavorazione</Strong> — l'impasto, i prosciutti in
        stagionatura, le forme in maturazione — non le materie prime ricevute. Quelle stanno in{' '}
        <Code>Magazzino materie prime</Code>, e rispondono a un'altra domanda.
      </P>

      <Callout kind="info" titolo="Le due domande, per non confonderle">
        <Strong>Magazzino materie prime</Strong> risponde a <em>cosa ho comprato e quanto ne
        resta</em>, ordinato per scadenza. <Strong>Giacenze per ubicazione</Strong> risponde a{' '}
        <em>dove si trova quello che sto producendo</em>, ordinato per cella. Sono due schermate
        diverse perché sono due momenti diversi della vita del prodotto.
      </Callout>

      <P>
        A cosa serve davvero, in pratica: fare il giro delle celle senza girare le celle, sapere
        se in cella 3 c'è ancora posto, e — il caso che capita sempre — ritrovare un lotto che
        qualcuno ha spostato senza dirlo.
      </P>

      {/* g1 */}
      <H id="g1" cat="produzione">1. Da dove arrivano le righe</H>

      <P>
        Qui non si registra niente di nuovo: <Strong>questa schermata mostra quello che è stato
        dichiarato nelle lavorazioni</Strong>. Ogni riga nasce da un gesto fatto altrove.
      </P>

      <Passo n="1" titolo="Avviando una fase">
        <P style={{ margin: '4px 0' }}>
          Quando avviate una fase e indicate dove mettete il prodotto — quanti pezzi, quanti
          chili, in quale ubicazione — nasce la riga.
        </P>
      </Passo>

      <Passo n="2" titolo="Spostando il prodotto">
        <P style={{ margin: '4px 0' }}>
          Da dentro la lavorazione con <Tasto>Sposta prodotto</Tasto>, o da questa stessa
          schermata con <Tasto>Sposta</Tasto>: la quantità esce da una cella ed entra
          nell'altra.
        </P>
      </Passo>

      <Passo n="3" titolo="Chiudendo la lavorazione">
        <P style={{ margin: '4px 0' }}>
          Nella sezione <Strong>Dove resta il prodotto</Strong> dichiarate dove finisce il
          prodotto finito. Quelle righe restano qui anche dopo la chiusura, ed è giusto così:
          il prosciutto è ancora in cella.
        </P>
      </Passo>

      <Callout kind="tip" titolo="Se una cella è vuota non compare">
        L'elenco mostra solo le righe con qualcosa dentro. Quando una fase esce del tutto da
        un'ubicazione, quella riga sparisce da sola: non c'è niente da chiudere o archiviare a
        mano.
      </Callout>

      <P>
        Le celle si creano una volta in <Code>Tracciabilità</Code> · <Code>Configurazione</Code>{' '}
        · <Code>Ubicazioni</Code>, con un nome e una descrizione. Una che non usate più si
        archivia togliendo la spunta <Tasto>Ubicazione attiva</Tasto>: sparisce dalle tendine ma
        resta nello storico.
      </P>

      {/* g2 */}
      <H id="g2" cat="produzione">2. Leggere la tabella</H>

      <MockFrame
        url="app.labthree.it — Giacenze per ubicazione"
        markers={[
          { n: 1, top: '18%', left: '30%', titolo: 'I quattro totali',
            testo: 'Si ricalcolano sui filtri: filtrando una sola cella, i totali diventano quelli di quella cella.' },
          { n: 2, top: '72%', left: '30%', titolo: 'Il riferimento della lavorazione',
            testo: 'Sotto il lotto. È il modo per distinguere due righe che altrimenti sarebbero identiche.' },
          { n: 3, top: '72%', left: '93%', titolo: 'Sposta',
            testo: 'Sposta questa riga in un’altra cella senza entrare nella lavorazione.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Giacenze per ubicazione"
            sottotitolo="Dove si trova adesso il prodotto, in lavorazione e finito. Da qui lo puoi spostare."
          />

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12,
          }}>
            {[['Ubicazioni', '4'], ['Righe', '7'], ['Pezzi totali', '612'], ['Peso totale', '7 940.50 kg']].map(([lab, n]) => (
              <div key={lab} style={{
                background: LT.n0, border: `1px solid ${LT.n200}`,
                borderRadius: LT.r.md, padding: '9px 11px',
              }}>
                <div style={{ fontSize: 10, color: LT.n500, fontWeight: 600 }}>{lab}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: LT.navy }}>{n}</div>
              </div>
            ))}
          </div>

          <div style={{
            background: LT.n0, border: `1px solid ${LT.n200}`,
            borderRadius: LT.r.md, overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: LT.n50 }}>
                  <TH>Ubicazione</TH>
                  <TH>Prodotto</TH>
                  <TH>Lotto</TH>
                  <TH>Fase</TH>
                  <TH destra>Pz</TH>
                  <TH destra>Peso (kg)</TH>
                  <TH destra>Sposta</TH>
                </tr>
              </thead>
              <tbody>
                {[
                  { ub: 'Cella 1 — sale', pr: 'Prosciutto crudo', lot: 'L. 23 PT', lav: 'lav. #214 del 12/06/2026', fase: '1° salatura', pz: '196', kg: '2 410.00' },
                  { ub: 'Cella 7', pr: 'Prosciutto crudo', lot: 'L. 23 PT', lav: 'lav. #214 del 12/06/2026', fase: 'Riposo', pz: '154', kg: '1 880.50' },
                  { ub: 'Cella 7', pr: 'Prosciutto crudo', lot: 'L. 26 PT', lav: 'lav. #227 del 03/07/2026', fase: 'Riposo', pz: '132', kg: '1 640.00' },
                ].map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < 2 ? `1px solid ${LT.n100}` : 'none' }}>
                    <TD style={{ fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Ico d={I.pin} size={13} stroke={LT.arancio} />{r.ub}
                      </span>
                    </TD>
                    <TD>{r.pr}</TD>
                    <TD style={{ fontFamily: LT.mono, fontSize: 11.5, color: LT.n700 }}>
                      {r.lot}
                      <div style={{ fontFamily: LT.sans, fontSize: 10.5, color: LT.n500, marginTop: 2 }}>
                        {r.lav}
                      </div>
                    </TD>
                    <TD><ChipFinto tono="verde">{r.fase}</ChipFinto></TD>
                    <TD destra style={{ fontWeight: 600 }}>{r.pz}</TD>
                    <TD destra style={{ fontWeight: 600 }}>{r.kg}</TD>
                    <TD destra><BtnFinto>Sposta</BtnFinto></TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Due filtri, <Tasto>Prodotto</Tasto> e <Tasto>Collocazione</Tasto>, che cercano dentro il
        testo: scrivere <Code>cella 7</Code> basta a isolare quella cella. I quattro totali in
        alto si ricalcolano su quello che resta filtrato, quindi si può usarli per pesare una
        cella sola.
      </P>

      <Callout kind="info" titolo="Perché lo stesso lotto compare più volte">
        Guardate le prime due righe dell'esempio: stesso lotto <Code>L. 23 PT</Code>, due celle
        diverse, due fasi diverse. È normale — un lotto grosso si divide, e le fasi possono
        convivere. Una riga è la combinazione <Strong>ubicazione + fase</Strong>, non il lotto.
      </Callout>

      {/* g3 */}
      <H id="g3" cat="produzione">3. Perché sotto il lotto c'è un numero</H>

      <P>
        Sotto ogni lotto compare una riga piccola: <Code>lav. #214 del 12/06/2026</Code>, e se la
        lavorazione è finita, un <Code>· chiusa</Code> in coda.
      </P>
      <P>
        Il motivo è concreto. Il lotto è generato dalla <Strong>data</Strong>, quindi{' '}
        <Strong>due lavorazioni avviate lo stesso giorno hanno lo stesso lotto</Strong>. Senza il
        numero della lavorazione, due righe dello stesso prodotto nello stesso giorno sarebbero
        indistinguibili, e uno spostamento potrebbe finire su quella sbagliata.
      </P>

      <Callout kind="tip" titolo="Il numero è la chiave per ritrovare tutto">
        È lo stesso numero che vedete nell'elenco delle lavorazioni. Se una riga non vi torna,
        cercatelo lì: da quella schermata si vedono i lotti di materia prima usati, le fasi e
        tutti i movimenti di ubicazione.
      </Callout>

      {/* g4 */}
      <H id="g4" cat="produzione">4. Spostare in un'altra cella</H>

      <P>
        Il pulsante <Tasto>Sposta</Tasto> a fine riga apre la finestra{' '}
        <Strong>Sposta prodotto</Strong>. È lo stesso movimento che si fa da dentro la
        lavorazione, con gli stessi controlli: da qui è solo più veloce, perché si parte da dove
        la merce è invece che da quale produzione è.
      </P>

      <MockFrame
        url="app.labthree.it — Sposta prodotto"
        markers={[
          { n: 1, top: '20%', left: '50%', titolo: 'Il riepilogo',
            testo: 'Prodotto, lotto, numero di lavorazione, fase e quanto c’è nell’ubicazione di partenza. Serve a non sbagliare riga.' },
          { n: 2, top: '58%', left: '50%', titolo: 'Pezzi e peso',
            testo: 'Precompilati con tutto quello che c’è. Se state spostando solo una parte, abbassateli.' },
          { n: 3, top: '82%', left: '50%', titolo: 'Data e ora',
            testo: 'Precompilata con adesso. Si può correggere all’indietro, ma non prima dell’inizio della fase.' },
        ]}
      >
        <Schermo>
          <div style={{ fontSize: 17, fontWeight: 800, color: LT.navy, marginBottom: 12 }}>
            Sposta prodotto
          </div>

          <div style={{
            fontSize: 12.5, color: LT.n700, background: LT.n50,
            border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
            padding: '9px 11px', marginBottom: 14, lineHeight: 1.5,
          }}>
            <div><strong>Prosciutto crudo</strong></div>
            <div style={{ color: LT.n500 }}>lotto L. 23 PT · lav. #214 · 1° salatura</div>
            <div style={{ marginTop: 4 }}>
              Da <strong>Cella 1 — sale</strong> · disponibili 196 pz / 2410.00 kg
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <CampoFinto etichetta="A quale ubicazione" valore="Cella 3 — pre riposo" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
            <CampoFinto etichetta="Pezzi" valore="196" />
            <CampoFinto etichetta="Peso (kg)" valore="2410" />
          </div>
          <div style={{ fontSize: 10.5, color: LT.n500, marginBottom: 10 }}>
            Precompilati con tutto quello che c'è nell'ubicazione. Abbassali se stai spostando
            solo una parte.
          </div>
          <CampoFinto
            etichetta="Data e ora dello spostamento"
            valore="18/08/2026 09:40"
            aiuto="Precompilata con l'ora attuale. Si può correggere, ma non prima dell'inizio della fase."
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <BtnFinto>Annulla</BtnFinto>
            <BtnFinto kind="blu">Conferma spostamento</BtnFinto>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Ci sono due sole cose che il programma rifiuta, e in tutti e due i casi la finestra resta
        aperta perché correggere costi un attimo.
      </P>

      <Callout kind="warn" titolo="“Quantità da spostare superiore alla giacenza dell'ubicazione”">
        Non si può spostare più di quello che c'è. Il messaggio vi dice esattamente quanto è
        disponibile, in pezzi e in chili. Capita quasi sempre perché si è lasciato il valore
        precompilato dopo aver già spostato una parte.
      </Callout>

      <Callout kind="warn" titolo="“La data indicata è precedente all'inizio della fase”">
        Un prodotto non può muoversi prima di esistere in quella fase. Il messaggio riporta data
        e ora di inizio fase, così sapete qual è il limite. Se la data giusta è davvero
        precedente, allora il problema è l'ora di inizio della fase, e va corretta lì.
      </Callout>

      <Callout kind="tip" titolo="Pezzi e chili sono indipendenti">
        Potete compilarne uno solo. Se contate a pezzi e non pesate a ogni spostamento, lasciate
        i chili vuoti: il programma non vi obbliga a inventare un peso.
      </Callout>

      {/* g5 */}
      <H id="g5" cat="produzione">5. Quello che resta dopo la chiusura</H>

      <P>
        Chiudendo una lavorazione dichiarate <Strong>dove resta il prodotto</Strong>. Quelle
        righe continuano a comparire qui, marcate <Code>· chiusa</Code> sotto il lotto.
      </P>
      <P>
        È voluto, e per le lavorazioni lunghe è il motivo principale per cui questa schermata
        esiste: un prosciutto sta in cella diciotto mesi <em>dopo</em> che la lavorazione è
        stata chiusa. Se le righe chiuse sparissero, la schermata mostrerebbe una cantina vuota
        mentre è piena.
      </P>
      <P>
        Anche il prodotto finito si sposta: <Tasto>Sposta</Tasto> funziona allo stesso modo su
        una riga di lavorazione chiusa. Le righe spariscono solo quando la quantità arriva a
        zero — cioè quando il prodotto esce davvero, con una bolla di uscita o uno scarto.
      </P>

      {/* g6 */}
      <H id="g6" cat="produzione">6. La merce di terzi</H>

      <P>
        Se la lavorazione è in <Strong>conto lavorazione</Strong>, sotto il nome del prodotto
        compare il contrassegno arancione{' '}
        <ChipFinto tono="arancio">Conto terzi · nome</ChipFinto>, e fra i filtri si aggiunge{' '}
        <Tasto>Proprietà</Tasto> con <Tasto>Solo merce mia</Tasto> e{' '}
        <Tasto>Solo merce di terzi</Tasto>.
      </P>
      <P>
        Su questa schermata è la funzione più utile delle tre in cui compare: in una cantina dove
        stagionano insieme i prosciutti di tre committenti diversi e i vostri, è l'unico modo per
        vedere in un colpo cosa è di chi prima di preparare un ritiro.
      </P>

      <Callout kind="info" titolo="Se non lavorate conto terzi non cambia nulla">
        Contrassegno e filtro compaiono solo se in azienda esiste almeno una lavorazione con un
        committente.
      </Callout>

      {/* g7 */}
      <H id="g7">Le trappole</H>

      <Callout kind="warn" titolo="Da qui non si scarta">
        Lo <Strong>scarto</Strong> — buttare via del prodotto — si fa da dentro la lavorazione,
        perché richiede una motivazione obbligatoria e apre una non conformità. Qui c'è solo lo
        spostamento, che è un movimento senza giudizio.
      </Callout>

      <Callout kind="warn" titolo="Non è un inventario">
        I numeri sono quelli dichiarati, non pesati. Se in cella ci sono meno pezzi di quelli che
        legge la schermata, il programma non se ne accorge: qualcuno ha spostato o scartato senza
        registrarlo. La schermata è affidabile quanto le dichiarazioni che l'hanno riempita.
      </Callout>

      <Callout kind="warn" titolo="Il trattino non vuol dire zero">
        Nelle colonne Pz e Peso un <Code>—</Code> vuol dire <em>non dichiarato</em>, non{' '}
        <em>zero</em>. Chi conta a pezzi e non pesa vedrà sempre un trattino nella colonna del
        peso, e va bene così.
      </Callout>

      <Callout kind="warn" titolo="Una cella archiviata continua a mostrare quello che contiene">
        Togliere la spunta <Tasto>Ubicazione attiva</Tasto> la toglie dalle tendine di
        destinazione, non dalle giacenze. Se dentro c'è ancora prodotto, la riga resta finché non
        lo spostate altrove.
      </Callout>

      {/* g8 */}
      <H id="g8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ho spostato la roba in cella ieri e mi sono dimenticato di registrarlo.">
          Registratelo adesso correggendo <Strong>data e ora</Strong> nella finestra di
          spostamento: il campo è modificabile all'indietro fino all'inizio della fase. Il
          movimento risulterà con la data vera, che è quello che conta.
        </FAQ>

        <FAQ q="Ho sbagliato cella di destinazione. Come annullo?">
          Non c'è un annulla: si fa lo spostamento contrario, dalla cella sbagliata a quella
          giusta. Restano registrati due movimenti invece di uno — è corretto che sia così, i due
          spostamenti sono davvero avvenuti nel registro.
        </FAQ>

        <FAQ q="Perché non trovo le materie prime qui dentro?">
          Perché non ci sono. Questa schermata segue il prodotto <Strong>in lavorazione</Strong>,
          che nasce quando si avvia una fase. Le materie prime ricevute stanno in{' '}
          <Code>Magazzino materie prime</Code> e non hanno un'ubicazione: LabThree non traccia in
          che scaffale è il sacco di sale.
        </FAQ>

        <FAQ q="Una riga è sparita e non ho spostato niente.">
          Sparisce quando la quantità arriva a zero. Le tre cause, in ordine: qualcuno ha chiuso
          la fase facendo uscire tutto, è stato registrato uno scarto, oppure il prodotto è
          uscito con una bolla. L'elenco dei movimenti si vede dentro la lavorazione, dal
          riquadro delle posizioni.
        </FAQ>

        <FAQ q="Posso vedere lo storico degli spostamenti di un lotto?">
          Non da qui: questa è la fotografia di adesso. Lo storico completo — ogni entrata,
          spostamento e scarto con data e ora — sta nella schermata della lavorazione, e finisce
          per intero nel test di tracciabilità.
        </FAQ>

        <FAQ q="Un operatore base può spostare?">
          Sì. È un gesto di reparto: chi sposta il carrello è chi deve poterlo registrare, senza
          passare dall'ufficio. Come ogni movimento resta scritto con data e ora.
        </FAQ>

        <FAQ q="Quante ubicazioni conviene creare?">
          Quante ne distinguete davvero quando parlate fra voi. Se in reparto dite "in cella 3",
          allora cella 3 è un'ubicazione. Se dite "di là", non serve spaccarla in quattro
          scaffali: ubicazioni troppo fini si dimenticano di aggiornare, e una giacenza non
          aggiornata è peggio di una giacenza grossolana.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Magazzino prodotti finiti e bolle di uscita</Strong>: il collegamento fra il
            lotto e il cliente, cioè quello che serve davvero il giorno di un richiamo.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
